import React, { useState, useRef, useCallback, useEffect } from 'react'
import { IconButton, Tooltip, Typography, LinearProgress } from '@material-ui/core'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import CloseIcon from '@material-ui/icons/Close'
import axios from 'axios'
import UploadCategoryWizard from './UploadCategoryWizard'
import PlanPageSelector from '../Documents/PlanPageSelector'
import { subscribe } from '../../SocketIo/Socket'

const overlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(46, 125, 50, 0.85)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
  transition: 'opacity 0.2s ease'
}

const overlayTextStyle = {
  color: '#fff',
  fontSize: 28,
  fontWeight: 600,
  marginTop: 16
}

const overlaySubTextStyle = {
  color: 'rgba(255,255,255,0.8)',
  fontSize: 14,
  marginTop: 8
}

const overlayIconStyle = {
  color: '#fff',
  fontSize: 64
}

// ── Background progress pill ─────────────────────────────────────
// Fixed to bottom-right, shows upload + parse progress, auto-dismisses.
const progressPillStyle = {
  position: 'fixed',
  bottom: 24,
  right: 24,
  zIndex: 1400,
  backgroundColor: '#fff',
  borderRadius: 12,
  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
  padding: '12px 16px',
  minWidth: 300,
  maxWidth: 400
}

const UploadDropzone = React.forwardRef(({ projectId, url, onUploadComplete }, ref) => {
  const [dragOver, setDragOver] = useState(false)
  const [wizardFile, setWizardFile] = useState(null)
  const fileInputRef = useRef()
  const dragCounterRef = useRef(0)

  // Background upload state
  const [bgJob, setBgJob] = useState(null)
  // bgJob shape: { fileName, category, status, progress, error, result }
  // status: 'uploading' | 'analyzing' | 'classified' | 'done' | 'error'

  // Plan page selector
  const [pageSelectorDoc, setPageSelectorDoc] = useState(null)

  const handleDragEnter = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current += 1
    if (dragCounterRef.current === 1) setDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current -= 1
    if (dragCounterRef.current === 0) setDragOver(false)
  }, [])

  const handleDragOver = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragOver(false)
    dragCounterRef.current = 0
    const files = e.dataTransfer?.files
    if (files && files.length > 0) setWizardFile(files[0])
  }, [])

  const handleButtonClick = () => {
    fileInputRef.current?.click()
  }

  // Expose triggerUpload so parent/sibling components can open the file picker
  React.useImperativeHandle(ref, () => ({
    triggerUpload: handleButtonClick
  }))

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0]
    if (file) setWizardFile(file)
    e.target.value = ''
  }

  const handleWizardClose = () => {
    setWizardFile(null)
  }

  // Called when user picks a category — wizard closes, upload starts in background
  const handleCategoryConfirmed = (category) => {
    const file = wizardFile
    setWizardFile(null) // Close the wizard immediately

    setBgJob({
      fileName: file.name,
      category,
      status: 'uploading',
      progress: 0,
      error: null,
      result: null,
      startTime: Date.now()
    })

    const formData = new FormData()
    formData.append('file', file)
    formData.append('category', category)

    axios.post(
      `${url || ''}/api/bid-estimation/projects/${projectId}/documents`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
        onUploadProgress: (e) => {
          if (e.total) {
            const pct = Math.round((e.loaded / e.total) * 100)
            setBgJob(prev => prev ? { ...prev, progress: pct, status: pct < 100 ? 'uploading' : 'analyzing' } : prev)
          }
        }
      }
    ).then(({ data }) => {
      // Plans: classified → show page selector
      if (category === 'plans' && data.parseStatus === 'classified' && data.parseResult?.documentType === 'plan_set') {
        const pc = data.parseResult?.pageCount
        setBgJob(prev => {
          if (!prev) return prev
          const elapsed = prev.startTime ? ((Date.now() - prev.startTime) / 1000).toFixed(1) : null
          return {
            ...prev,
            status: 'classified',
            result: data,
            message: `${pc} pages classified${elapsed ? ` in ${elapsed}s` : ''}`
          }
        })
        return
      }

      // Plans: small set fully parsed
      if (category === 'plans' && data.parseStatus === 'parsed' && data.parseResult?.documentType === 'plan_set') {
        setBgJob(prev => prev ? { ...prev, status: 'done', result: data, message: 'Plan set parsed' } : prev)
        if (onUploadComplete) onUploadComplete({ ...data, category })
        autoDismiss()
        return
      }

      // Vendor quote
      if (category === 'vendor_quote' && data.vendorQuote) {
        setBgJob(prev => prev ? { ...prev, status: 'done', result: data } : prev)
        if (onUploadComplete) onUploadComplete({ ...data, category, action: 'open_quote_review' })
        autoDismiss()
        return
      }

      // Bid response
      if (category === 'bid_response' && data.bidPackageId) {
        setBgJob(prev => prev ? { ...prev, status: 'done', result: data } : prev)
        if (onUploadComplete) onUploadComplete({ ...data, category, action: 'open_bid_response' })
        autoDismiss()
        return
      }

      // Everything else
      setBgJob(prev => prev ? { ...prev, status: 'done', result: data } : prev)
      if (onUploadComplete) onUploadComplete({ ...data, category })
      autoDismiss()
    }).catch((err) => {
      setBgJob(prev => prev ? {
        ...prev,
        status: 'error',
        error: err.response?.data?.error || err.message || 'Upload failed'
      } : prev)
    })
  }

  const autoDismiss = () => {
    setTimeout(() => setBgJob(null), 3000)
  }

  const handlePagesParsed = (updatedDoc) => {
    setPageSelectorDoc(null)
    setBgJob(prev => prev ? { ...prev, status: 'done', message: 'Schedules extracted' } : prev)
    if (onUploadComplete) onUploadComplete({ ...updatedDoc, category: 'plans' })
    autoDismiss()
  }

  // Subscribe to socket progress events during analysis
  useEffect(() => {
    if (!projectId || !bgJob || (bgJob.status !== 'analyzing' && bgJob.status !== 'uploading')) return
    const unsub = subscribe(`doc_upload_${projectId}`, (data) => {
      if (!data || !data.stage) return
      if (data.stage === 'complete') return // HTTP response handles completion
      const message = data.message || data.stage
      setBgJob(prev => prev && (prev.status === 'analyzing' || prev.status === 'uploading')
        ? { ...prev, status: 'analyzing', progressMessage: message }
        : prev
      )
    })
    return unsub
  }, [projectId, bgJob?.status])

  // Attach drag listeners to the whole document
  React.useEffect(() => {
    document.addEventListener('dragenter', handleDragEnter)
    document.addEventListener('dragleave', handleDragLeave)
    document.addEventListener('dragover', handleDragOver)
    document.addEventListener('drop', handleDrop)
    return () => {
      document.removeEventListener('dragenter', handleDragEnter)
      document.removeEventListener('dragleave', handleDragLeave)
      document.removeEventListener('dragover', handleDragOver)
      document.removeEventListener('drop', handleDrop)
    }
  }, [handleDragEnter, handleDragLeave, handleDragOver, handleDrop])

  const statusLabel = bgJob ? {
    uploading: `Uploading ${bgJob.fileName}…`,
    analyzing: bgJob.progressMessage || `Analyzing ${bgJob.fileName}…`,
    classified: bgJob.message || 'Plan set classified',
    done: bgJob.message || 'Upload complete',
    error: bgJob.error || 'Upload failed'
  }[bgJob.status] : ''

  return (
    <>
      {/* Upload button for the header */}
      <Tooltip title="Upload File">
        <IconButton
          size="small"
          onClick={handleButtonClick}
          style={{ color: '#fff' }}
        >
          <CloudUploadIcon />
        </IconButton>
      </Tooltip>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      {/* Drag overlay */}
      {dragOver && (
        <div style={overlayStyle}>
          <CloudUploadIcon style={overlayIconStyle} />
          <div style={overlayTextStyle}>Drop to Upload</div>
          <div style={overlaySubTextStyle}>Release to upload your file to this project</div>
        </div>
      )}

      {/* Category wizard — only picks category, no upload */}
      {wizardFile && (
        <UploadCategoryWizard
          file={wizardFile}
          onClose={handleWizardClose}
          onCategoryConfirmed={handleCategoryConfirmed}
        />
      )}

      {/* ── Background progress pill ────────────────────── */}
      {bgJob && (
        <div style={progressPillStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: bgJob.status === 'uploading' ? 8 : 0 }}>
            {bgJob.status === 'done' && <CheckCircleIcon style={{ color: '#2e7d32', fontSize: 20 }} />}
            {bgJob.status === 'error' && <ErrorOutlineIcon style={{ color: '#c62828', fontSize: 20 }} />}
            {(bgJob.status === 'uploading' || bgJob.status === 'analyzing') && (
              <CloudUploadIcon style={{ color: '#1976d2', fontSize: 20 }} />
            )}
            {bgJob.status === 'classified' && (
              <CheckCircleIcon style={{ color: '#1976d2', fontSize: 20 }} />
            )}

            <span style={{
              flex: 1,
              fontSize: 13,
              fontWeight: 500,
              color: '#333',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {statusLabel}
            </span>

            {/* Action button for classified plans */}
            {bgJob.status === 'classified' && bgJob.result && (
              <button
                onClick={() => { setPageSelectorDoc(bgJob.result); setBgJob(null) }}
                style={{
                  backgroundColor: '#1976d2',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  padding: '4px 12px',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                Select Pages
              </button>
            )}

            {/* Dismiss button */}
            {(bgJob.status === 'done' || bgJob.status === 'error' || bgJob.status === 'classified') && (
              <IconButton size="small" onClick={() => setBgJob(null)} style={{ padding: 2 }}>
                <CloseIcon style={{ fontSize: 16 }} />
              </IconButton>
            )}
          </div>

          {/* Progress bar for upload */}
          {(bgJob.status === 'uploading' || bgJob.status === 'analyzing') && (
            <LinearProgress
              variant={bgJob.status === 'uploading' ? 'determinate' : 'indeterminate'}
              value={bgJob.progress}
              style={{ height: 4, borderRadius: 2 }}
            />
          )}
        </div>
      )}

      {/* Plan page selector */}
      {pageSelectorDoc && (
        <PlanPageSelector
          open
          onClose={() => {
            setPageSelectorDoc(null)
            // If they close without extracting, still save as reference
            if (bgJob?.result && onUploadComplete) {
              onUploadComplete({ ...bgJob.result, category: 'plans' })
            }
            setBgJob(null)
          }}
          doc={pageSelectorDoc}
          projectId={projectId}
          url={url}
          onParsed={handlePagesParsed}
        />
      )}
    </>
  )
})

export default UploadDropzone
